const REDACTED = '[REDACTED]';
// Keep the noise filter small and predictable. See Sentry "ignoreErrors" docs.
const DEFAULT_IGNORE_ERRORS = [
    'Network Error',
    'Failed to fetch',
    'AbortError',
];
// Redact common sensitive fields before events leave the app. This complements
// Sentry's `beforeSend` hook from the official SDK docs.
const SENSITIVE_KEY_PATTERN = /email|phone|telephone|password|token|authorization|cookie|set-cookie|dsn/i;
function sanitizeValue(value) {
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((item) => sanitizeValue(item));
    }
    if (value && typeof value === 'object') {
        return sanitizeObject(value);
    }
    return value;
}
function sanitizeObject(obj) {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
        if (SENSITIVE_KEY_PATTERN.test(key)) {
            sanitized[key] = REDACTED;
            continue;
        }
        sanitized[key] = sanitizeValue(value);
    }
    return sanitized;
}
export function shouldEnableSentry() {
    // Single source of truth for Sentry enablement across frontend and backend.
    // We require an explicit opt-in so local/staging environments do not report
    // accidentally, even if a DSN is present.
    return (process.env.SENTRY_ENABLED === 'true' &&
        !!process.env.SENTRY_DSN &&
        !!process.env.NODE_ENV &&
        !!process.env.SITE_ROOT);
}
export function getSentryRelease() {
    // Sentry groups issues more usefully when release is stable and matches the
    // deployed app version. See Sentry "Releases" docs.
    return `${process.env.npm_package_name}@${process.env.VERSION || process.env.npm_package_version}`;
}
export function getSentryEnvironment() {
    // Use the app runtime env directly for Sentry environment tagging.
    return process.env.NODE_ENV;
}
export function getSentryIgnoreErrors() {
    return DEFAULT_IGNORE_ERRORS;
}
export function sanitizeSentryEvent(event) {
    // This is intentionally conservative: if a key looks sensitive, redact it.
    // Official Sentry guidance recommends using `beforeSend` for last-mile
    // event scrubbing before data is sent to Sentry.
    const sanitized = { ...event };
    if (sanitized.user) {
        sanitized.user = sanitizeObject(sanitized.user);
    }
    if (sanitized.request) {
        sanitized.request = sanitizeObject(sanitized.request);
    }
    if (sanitized.extra) {
        sanitized.extra = sanitizeObject(sanitized.extra);
    }
    if (sanitized.contexts) {
        sanitized.contexts = sanitizeObject(sanitized.contexts);
    }
    if (sanitized.breadcrumbs) {
        sanitized.breadcrumbs = sanitized.breadcrumbs.map((breadcrumb) => sanitizeObject(breadcrumb));
    }
    sanitized.tags = {
        ...(sanitized.tags || {}),
        app: process.env.APP_NAME || 'unknown',
        node_env: process.env.NODE_ENV || 'unknown',
        site_root: process.env.SITE_ROOT || 'unknown',
    };
    return sanitized;
}
// Shared base config so backend and frontend stay aligned.
export function getSentryBaseOptions() {
    // These map directly to standard Sentry SDK init options:
    // `dsn`, `environment`, `release`, `beforeSend`, and `ignoreErrors`.
    // See the Sentry JavaScript SDK configuration docs for details.
    return {
        dsn: process.env.SENTRY_DSN,
        environment: getSentryEnvironment(),
        release: getSentryRelease(),
        beforeSend(event) {
            return sanitizeSentryEvent(event);
        },
        ignoreErrors: getSentryIgnoreErrors(),
    };
}
//# sourceMappingURL=sentry-config.js.map